import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FloatingBackToTop from '@/components/feature/FloatingBackToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticlesPage() {
  const { t } = useTranslation();

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="精选文章 - 岛国精品｜日本商品深度解析与推荐"
        description="阅读岛国精品为您精选的日本商品相关文章，涵盖美妆护肤技巧、食品文化、生活指南等丰富内容。深度了解日本好物。"
        keywords="岛国精品文章,日本商品推荐,美妆护肤技巧,日本零食推荐,日本文化,日式收纳"
        canonicalUrl="https://www.daoguojingpin.com/articles"
        ogType="website"
      />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.daoguojingpin.com' },
          { '@type': 'ListItem', position: 2, name: '精选文章', item: 'https://www.daoguojingpin.com/articles' },
        ],
      }} />

      <SchemaOrg type="WebPage" data={{
        name: '精选文章 - 岛国精品',
        url: 'https://www.daoguojingpin.com/articles',
        description: '阅读岛国精品精选文章，深入了解日本商品与文化',
        inLanguage: 'zh-CN',
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 md:pt-20">
          <Breadcrumb items={[{ label: t('pageTitle') }]} />

          <section className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-2">
                {t('pageTitle')}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base mb-8 max-w-2xl">
                {t('pageDescription')}
              </p>

              {articles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group bg-background-100 rounded-lg overflow-hidden hover:bg-background-200/70 transition-all duration-500"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                            {article.category}
                          </span>
                          <span className="text-xs text-foreground-400 flex items-center gap-1">
                            <i className="ri-time-line"></i>
                            {article.readTime}
                          </span>
                        </div>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-300 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-400">{article.publishedAt}</span>
                          <span className="text-xs text-primary-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                            {t('readMore')}
                            <i className="ri-arrow-right-line"></i>
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-foreground-500 text-sm">{t('emptyArticles')}</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
        <FloatingBackToTop />
      </div>
    </>
  );
}